/* eslint-disable max-statements */

import { Group, Menu, UnstyledButton } from '@mantine/core';
import { merge } from 'lodash';
import { useEffect, useState } from 'react';

import { BatchTypeEnum, EnvironmentCountries, TransitGroup, TSDType } from '@e-origin/shared';

import { actionsIcon, downloadIcon, IconFactory } from '@assets';
import { useTriggerBatchNotification } from '@hooks/batch';
import { useSettings } from '@hooks/useSettings';
import classes from './TableActions.module.css';
import {
  formatActionText,
  getHighValuesExportOptions,
  getHighValuesImportOptions,
  getHighValuesOptions,
  getLowValuesOptions,
  getTransitArrivalOptions,
  getTransitDepartureOptions,
  getTsdCombinedOptions,
  getTsdPrelodgedOptions,
  getTsdTransferredOptions,
  SIMULATE_RESPONSE,
} from './TableActionsConstants';

export enum TableActionEnum {
  Create = 'Create',
  View = 'view',
  Archive = 'archive',
  Cleanup = 'cleanup',
  CustomsSync = 'customsSync',
  Delete = 'delete',
  DownloadFile = 'downloadFile',
  DownloadImage = 'downloadImage',
  DownloadImageDisabled = 'downloadImageDisabled',
  Edit = 'edit',
  Send = 'send',
  GenerateCustomsReport = 'generateCustomsReport',
  GenerateRiskAnalysisReport = 'generateRiskAnalysisReport',
  GenerateInvoice = 'generateInvoice',
  ExportScreenshots = 'exportScreenshots',
  ReExecuteAction = 'reExecuteAction',
  Refactor = 'refactor',
  SendRiskAnalysisReport = 'sendRiskAnalysisReport',
  GenerateCustomerReport = 'generateCustomerReport',
  Unarchive = 'unarchive',
  Update = 'update',
  Finalize = 'finalize',
  ExecuteRiskAnalysis = 'executeRiskAnalysis',
  ReSendAction = 'ReSendAction',
  Regularization = 'Regularization',
  Duplicate = 'Duplicate',
  SetCustomsControl = 'SetCustomsControl',
  RiskAnalysisCalculations = 'RiskAnalysisCalculations',
  Publish = 'Publish',
  GenerateProformaB2BInvoice = 'GenerateProformaB2BInvoice',
  BatchAudit = 'BatchAudit',
  EnableSimulator = 'EnableSimulator',
  SimulateMessages = 'SimulateMessages',
}

export interface Action {
  id?: string;
  if?: boolean;
  icon?: string;
  iconComponent?: React.ReactNode;
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  disableAfterClick?: boolean;
  preventDefaultClick?: boolean;
  multi?: boolean;
  options?: Action[];
}

export interface TableActionsProps {
  actions: Action[];
  secondaryActions?: Action[];
  onClick?: (param?: string) => void;
  disableAfterClick?: boolean;
  smallIcons?: boolean;
  moveMenuUpwards?: boolean;
  inColumnView?: boolean;
  transitView?: TransitGroup;
  tsdView?: TSDType;
  showDebugMenu?: boolean;
  batchView?: BatchTypeEnum;
  'data-pw'?: string | number;
  batchdId?: string;
  children?: React.ReactNode;
}

export const TableActions: React.FC<TableActionsProps> = (props) => {
  const [actions, setActions] = useState<Action[]>([]);
  const [secondaryActions, setSecondaryActions] = useState<Action[]>([]);
  const { data: settings } = useSettings();

  const triggerBatchNotification = useTriggerBatchNotification();

  useEffect(() => {
    const mergedActions = (props.actions || []).map((newAction) => {
      const matchKey = newAction.id ?? newAction.text;
      const existingAction = actions.find((a) => (a.id ?? a.text) === matchKey);
      return merge(existingAction, newAction);
    });
    setActions(mergedActions);
  }, [props.actions]);

  useEffect(() => {
    const mergedSecondaryActions = (props.secondaryActions || []).map((newAction) => {
      const matchKey = newAction.id ?? newAction.text;
      const existingAction = secondaryActions.find((a) => (a.id ?? a.text) === matchKey);
      return merge(existingAction, newAction);
    });

    setSecondaryActions(mergedSecondaryActions);
  }, [props.secondaryActions]);

  const setDisableAction = (action: Action, disabled?: boolean, isPrimary = true) => {
    const updatedActions = (isPrimary ? actions : secondaryActions).map((act) => ({
      ...act,
      disabled: act === action ? disabled : act.disabled,
    }));
    if (isPrimary) {
      setActions(updatedActions);
      return;
    }

    setSecondaryActions(updatedActions);
  };

  const handleActionClick = (action: Action, isPrimary = true) => {
    const shouldDisable = action.disableAfterClick ?? props.disableAfterClick;

    if (shouldDisable) {
      setDisableAction(action, true, isPrimary);
    }

    action.onClick?.();

    if (!action.preventDefaultClick && props.onClick) {
      props.onClick();
    }
  };

  const generateButton = (action: Action, key?: string, showButtonText = false, isPrimary = true) => {
    if (action.multi && action.options?.length) {
      return (
        <Menu key={key} position="left-start">
          <Menu.Target>
            <UnstyledButton
              data-pw={`table-action-${formatActionText(action.text)}-btn-${props['data-pw']}`}
              title={!showButtonText ? action.text : undefined}
              className={`${classes.svgIcon} ${action.disabled ? classes.disabled : ''}`}
              disabled={!!action.disabled}
            >
              {action.icon && <img src={action.icon} alt={action.text.toLowerCase().split(' ').join('-')} />}
              {action.iconComponent && <>{action.iconComponent}</>}
              {showButtonText && <div>{action.text}</div>}
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            {action.options
              .filter((option) => [true, undefined].includes(option.if))
              .map((option, optionIndex) => (
                <Menu.Item
                  key={`${key}-option-${optionIndex}-${option.text}`}
                  data-pw={`table-action-${formatActionText(option.text)}-btn-${props['data-pw']}`}
                  leftSection={
                    option.icon ? (
                      <img src={option.icon} alt={option.text.toLowerCase().split(' ').join('-')} style={{ maxWidth: '22px' }} />
                    ) : (
                      option.iconComponent
                    )
                  }
                  disabled={!!option.disabled}
                  onClick={() => handleActionClick(option, isPrimary)}
                >
                  {option.text}
                </Menu.Item>
              ))}
          </Menu.Dropdown>
        </Menu>
      );
    }

    return (
      <UnstyledButton
        key={key}
        data-pw={`table-action-${formatActionText(action.text)}-btn-${props['data-pw']}`}
        title={!showButtonText ? action.text : undefined}
        className={`${classes.svgIcon} ${action.disabled ? classes.disabled : ''}`}
        onClick={() => handleActionClick(action, isPrimary)}
        disabled={!!action.disabled}
      >
        {action.icon && <img src={action.icon} alt={action.text.toLowerCase().split(' ').join('-')} />}
        {action.iconComponent && <>{action.iconComponent}</>}
        {showButtonText && <div>{action.text}</div>}
      </UnstyledButton>
    );
  };

  const renderTransitDebugMenu = () => {
    if (!settings?.isProd && props.showDebugMenu && props.transitView) {
      return (
        <Menu position="left-start">
          <Menu.Target>
            <UnstyledButton className={`${classes.svgIcon} ${classes.svgLightColor}`}>
              <img src={downloadIcon} alt="contextual" />
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            {(props.transitView === TransitGroup.DEPARTURE ? getTransitDepartureOptions() : getTransitArrivalOptions()).map(({ type, label }) => (
              <Menu.Item
                key={type}
                leftSection={<img src={downloadIcon} alt={SIMULATE_RESPONSE} />}
                onClick={() => props.onClick?.(`generateNCTSResponse-${type}`)}
              >
                {label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      );
    }

    return null;
  };

  const renderTsdDebugMenu = () => {
    let responseOptions = [];

    switch (props.tsdView) {
      case TSDType.COMBINED:
        responseOptions = getTsdCombinedOptions();
        break;
      case TSDType.PRELODGED:
        responseOptions = getTsdPrelodgedOptions();
        break;
      case TSDType.TRANSFERRED:
        responseOptions = getTsdTransferredOptions();
        break;
    }

    if (!settings?.isProd && props.showDebugMenu && props.tsdView) {
      return (
        <Menu position="left-start">
          <Menu.Target>
            <UnstyledButton className={`${classes.svgIcon} ${classes.svgLightColor}`}>
              <img src={downloadIcon} alt="contextual" />
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            {responseOptions.map(({ type, label }) => (
              <Menu.Item
                key={type}
                leftSection={<img src={downloadIcon} alt={SIMULATE_RESPONSE} />}
                onClick={() => props.onClick?.(`generateTsdResponse-${type}`)}
              >
                {label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      );
    }

    return null;
  };

  const renderNlDecoDebugMenu = () => {
    if (settings?.country === EnvironmentCountries.NL && !settings?.isProd && props.showDebugMenu) {
      return (
        <Menu position="left-start">
          <Menu.Target>
            <UnstyledButton className={`${classes.svgIcon} ${classes.svgLightColor}`}>
              <img src={IconFactory.actions} alt="contextual" />
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            {[
              ...(props.batchView === BatchTypeEnum.LOW_VALUES ? getLowValuesOptions() : []),
              ...(props.batchView === BatchTypeEnum.HIGH_VALUES ? getHighValuesOptions() : []),
            ].map(({ type, label }) => (
              <Menu.Item
                key={type}
                leftSection={<img src={IconFactory.download} alt={SIMULATE_RESPONSE} />}
                onClick={() => triggerBatchNotification.mutate({ batchId: props.batchdId, messageType: type })}
              >
                {label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      );
    }

    return null;
  };

  const renderIdmsHvDebugMenu = () => {
    if (
      settings?.country === EnvironmentCountries.BE &&
      !settings?.isProd &&
      props.showDebugMenu &&
      [BatchTypeEnum.HIGH_VALUES_EXPORT, BatchTypeEnum.HIGH_VALUES].includes(props.batchView)
    ) {
      return (
        <Menu position="left-start">
          <Menu.Target>
            <UnstyledButton className={`${classes.svgIcon} ${classes.svgLightColor}`}>
              <img src={IconFactory.actions} alt="contextual" />
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            {(props.batchView === BatchTypeEnum.HIGH_VALUES_EXPORT ? getHighValuesExportOptions() : getHighValuesImportOptions()).map(({ type, label }) => (
              <Menu.Item
                key={type}
                leftSection={<img src={IconFactory.download} alt={SIMULATE_RESPONSE} />}
                onClick={() => triggerBatchNotification.mutate({ batchId: props.batchdId, messageType: type })}
              >
                {label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      );
    }

    return null;
  };

  return (
    <Group gap="xs" wrap="nowrap" justify="flex-end">
      {actions
        ?.filter((action) => [true, undefined].includes(action.if))
        .map((action, index) => generateButton(action, `primary-${index}-${action.text}`, false))}

      {secondaryActions?.length > 0 && (
        <Menu position="left-start">
          <Menu.Target>
            <UnstyledButton className={classes.svgIcon} data-pw={`table-action-btns-${props['data-pw']}`}>
              <img src={actionsIcon} alt="contextual" />
            </UnstyledButton>
          </Menu.Target>

          <Menu.Dropdown>
            {secondaryActions
              ?.filter((action) => [true, undefined].includes(action.if))
              .map((action, index) => (
                <Menu.Item
                  key={`secondary-${index}-${action.text}`}
                  data-pw={`table-action-${formatActionText(action.text)}-btn`}
                  leftSection={
                    action.icon ? (
                      <img src={action.icon} alt={action.text.toLowerCase().split(' ').join('-')} style={{ maxWidth: '22px' }} />
                    ) : (
                      action.iconComponent
                    )
                  }
                  onClick={() => handleActionClick(action, false)}
                >
                  {action.text}
                </Menu.Item>
              ))}
          </Menu.Dropdown>
        </Menu>
      )}

      {renderTransitDebugMenu()}
      {renderNlDecoDebugMenu()}
      {renderIdmsHvDebugMenu()}
      {renderTsdDebugMenu()}
      {props.children}
    </Group>
  );
};
